// Libs
import React from 'react';
// Hooks
import { useOrderApi } from 'hooks';
// Components
import { Wrapper, FilesDragAndDrop } from 'components';
import { PROCESS_STATUS } from 'components/FilesDragAndDrop';
import {
	ReadOrderFromFiles,
	OrderFieldScraping,
	ImportUtils,
} from 'utils/Orders/ImportProcess';
import {
	assignImportRowIdentities,
	getImportRowIdentity,
	mergeImportDiagnostics,
	prepareImportOrdersForAssignment,
	hasSafeImportRowCorrelation,
} from './ImportPreview/importReview.model';

export const MAX_IMPORT_ORDERS = 500;

export const getImportErrorToast = error => {
	const envelope = error && typeof error === 'object' ? error : {};
	const nestedErrors = Array.isArray(error)
		? error
		: Array.isArray(envelope.errors)
			? envelope.errors
			: [];
	const firstError = nestedErrors[0] || {};
	return {
		message:
			(typeof error === 'string' ? error : undefined) ||
			envelope.message ||
			envelope.error ||
			firstError.message ||
			firstError.error ||
			'No fue posible procesar el archivo. Revisa sus datos e inténtalo nuevamente.',
		type: envelope.type || firstError.type || 'error',
	};
};

const normalizeConflictValue = value => String(value ?? '').trim();

export const getContractZoneReviewError = error => {
	const envelope = error && typeof error === 'object' ? error : {};
	const status = Number(envelope.httpStatus ?? envelope.status);
	const hasExpectedCode =
		envelope.code === 'IMPORT_REVIEW_STALE' ||
		envelope.name === 'IMPORT_REVIEW_STALE';
	const message = normalizeConflictValue(envelope.message);
	const conflicts = Array.isArray(envelope.contractZoneConflicts)
		? envelope.contractZoneConflicts
		: [];
	const normalizedConflicts = conflicts.map(conflict => ({
		contractNumber: normalizeConflictValue(conflict?.contractNumber),
		rowIds: Array.from(
			new Set(
				(Array.isArray(conflict?.rowIds) ? conflict.rowIds : [])
					.map(normalizeConflictValue)
					.filter(Boolean),
			),
		),
		zoneKeys: Array.from(
			new Set(
				(Array.isArray(conflict?.zoneKeys) ? conflict.zoneKeys : [])
					.map(normalizeConflictValue)
					.filter(Boolean),
			),
		),
	}));
	const returnedRowIds = new Set(
		(Array.isArray(envelope.orders) ? envelope.orders : [])
			.map(getImportRowIdentity)
			.filter(value => value !== undefined && value !== null)
			.map(String),
	);
	const hasValidConflicts =
		normalizedConflicts.length > 0 &&
		normalizedConflicts.every(
			conflict =>
				Boolean(conflict.contractNumber) &&
				conflict.rowIds.length > 1 &&
				conflict.zoneKeys.length > 1 &&
				conflict.rowIds.every(rowId => returnedRowIds.has(rowId)),
		);

	if (
		status !== 409 ||
		!hasExpectedCode ||
		envelope.retryable !== true ||
		!message ||
		!Array.isArray(envelope.orders) ||
		!envelope.orders.length ||
		!hasValidConflicts
	)
		return;

	return {
		message,
		contractZoneConflicts: normalizedConflicts,
	};
};

const readOrderFromFiles = async ({ files, boundary, setProcessing }) => {
	setProcessing({
		status: ImportUtils.getStatus().LOADING,
		message: `Leyendo ${files.length} archivo${files.length !== 1 ? 's' : ''}`,
	});
	return new ReadOrderFromFiles(files, boundary).run();
};

const orderFieldScraping = async ({
	xlsxOrders,
	txtOrders,
	fields,
	formComponentValues,
	setProcessing,
}) => {
	setProcessing({
		status: ImportUtils.getStatus().LOADING,
		message: 'Obteniendo datos',
	});
	return new OrderFieldScraping(
		{ xlsxOrders, txtOrders },
		fields,
		formComponentValues,
	).run();
};

const setOrderAssignments = ({
	orders,
	dateFormat,
	validationTemplateId,
	setProcessing,
	getCreationAssignment,
}) => {
	setProcessing({
		status: ImportUtils.getStatus().LOADING,
		message: 'Procesando datos',
	});
	return getCreationAssignment(orders, {
		dateFormat,
		validationTemplateId,
	});
};

const setDragAndDropStatus = status => {
	return {
		[ImportUtils.getStatus().LOADING]: PROCESS_STATUS.LOADING,
		[ImportUtils.getStatus().SUCCESS]: PROCESS_STATUS.SUCCESS,
		[ImportUtils.getStatus().UNSUCCESS]: PROCESS_STATUS.UNSUCCESS,
	}[status];
};

const ImportButton = ({
	disabled,
	dateFormat,
	templateValidator,
	profile,
	formComponentValues,
	onSetState,
	onResetOrders,
	sendToast,
}) => {
	const [processing, setProcessing] = React.useState({
		status: undefined,
		message: undefined,
	});
	const { getCreationAssignment } = useOrderApi();

	const handleChange = async files => {
		let identifiedOrders;
		try {
			if (!files || !files.length) return;

			// Reset income order states
			onResetOrders();

			const { xlsxOrders, txtOrders } = await readOrderFromFiles({
				files,
				boundary: templateValidator?.dataStructureObject?.boundary,
				setProcessing,
			});

			const scrapedOrders = await orderFieldScraping({
				xlsxOrders,
				txtOrders,
				fields: templateValidator?.dataStructureObject?.fields,
				formComponentValues,
				setProcessing,
			});

			if (!scrapedOrders.length) {
				sendToast({
					message: 'El archivo importado debe tener al menos un registro',
					type: 'warn',
				});
				onResetOrders();
				return;
			}
			if (scrapedOrders.length > MAX_IMPORT_ORDERS) {
				sendToast({
					message: `El archivo contiene ${scrapedOrders.length} órdenes. El máximo permitido por importación es ${MAX_IMPORT_ORDERS}.`,
					type: 'warn',
				});
				// Keep the importer ready for a corrected file. Orders were reset before
				// reading, so no oversized preview is retained locally.
				setProcessing({ status: undefined, message: undefined });
				return;
			}

			identifiedOrders = assignImportRowIdentities(scrapedOrders);
			const assignedOrders = await setOrderAssignments({
				orders: prepareImportOrdersForAssignment(identifiedOrders),
				dateFormat,
				validationTemplateId: templateValidator.id,
				setProcessing,
				getCreationAssignment,
			});
			if (!hasSafeImportRowCorrelation(assignedOrders, identifiedOrders)) {
				throw new Error(
					'La validación no devolvió identificadores de fila seguros. No se abrió una vista previa para evitar cruzar asignaciones.',
				);
			}

			onSetState({
				preview: true,
				orders: mergeImportDiagnostics(assignedOrders, identifiedOrders),
			});
		} catch (err) {
			const importReviewError = getContractZoneReviewError(err);
			if (
				importReviewError &&
				hasSafeImportRowCorrelation(err.orders, identifiedOrders)
			) {
				onSetState({
					preview: true,
					orders: mergeImportDiagnostics(err.orders, identifiedOrders),
					importReviewError,
				});
				return;
			}
			sendToast(getImportErrorToast(err));
			setProcessing({
				status: undefined,
			});
		}
	};

	return (
		<Wrapper width='100%' padding='0' margin='16px 0 0 0'>
			{profile?.organization?.algorithms?.orders?.import && (
				<FilesDragAndDrop
					disabled={disabled || !!processing.status}
					count={templateValidator?.dataStructureObject?.maxFiles}
					fileTypes={templateValidator?.dataStructureObject?.fileTypes}
					processing={{
						message: processing.message,
						status: setDragAndDropStatus(processing.status),
					}}
					handleChange={handleChange}
				/>
			)}
		</Wrapper>
	);
};

export default ImportButton;
